<template>
  <span class="field-history-button" @click.stop.prevent>
    <button
      ref="trigger"
      type="button"
      class="btn btn-sm btn-outline-primary py-0 px-2 align-middle field-history-trigger"
      :title="$t('fieldHistory.button')"
      :aria-label="$t('fieldHistory.button')"
      @click.stop.prevent="toggle"
    >
      <i class="fas fa-history" aria-hidden="true"></i>
    </button>
    <div
      v-if="open"
      ref="popup"
      class="field-history-popup card shadow border"
      :style="popupStyle"
      role="dialog"
    >
      <div class="card-header py-2 px-3 d-flex align-items-center justify-content-between">
        <strong class="small">{{ $t("fieldHistory.title", { field: $t(field.label) }) }}</strong>
        <button type="button" class="btn-close btn-sm" aria-label="Close" @click.stop.prevent="open = false"></button>
      </div>
      <div class="card-body p-2 small">
        <div v-if="loading" class="text-secondary p-2">{{ $t("fieldHistory.loading") }}</div>
        <div v-else-if="error" class="text-danger p-2">{{ $t("fieldHistory.error") }}</div>
        <div v-else-if="!changes.length" class="text-secondary p-2">{{ $t("fieldHistory.empty") }}</div>
        <template v-else>
          <div v-if="totalDiffLabel" class="px-2 pb-2 fw-semibold">{{ totalDiffLabel }}</div>
          <ul class="list-unstyled mb-0">
            <li
              v-for="(c, idx) in changes"
              :key="idx"
              class="field-history-row px-2 py-1"
            >
              <div class="d-flex align-items-baseline gap-2">
                <span class="fw-semibold">{{ formatValue(c.value) }}</span>
                <span v-if="idx > 0 && stepDiffLabel(idx)" class="text-secondary">({{ stepDiffLabel(idx) }})</span>
                <span v-if="idx === 0" class="badge bg-secondary ms-auto">{{ $t("fieldHistory.original") }}</span>
                <span v-else-if="idx === changes.length - 1" class="badge bg-primary ms-auto">{{ $t("fieldHistory.current") }}</span>
              </div>
              <div class="text-secondary">
                {{ formatSavedAt(c.savedAt) }} · {{ c.user || $t("fieldHistory.system") }}
              </div>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </span>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { formatDateSync, formatTimestampSync } from "@/utils/dateFormatter";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Día civil (YYYY-MM-DD) de una fecha guardada, sin correrlo por zona horaria. */
function calendarMs(value) {
  if (!value) return null;
  const s = value instanceof Date ? value.toISOString() : String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T12:00:00.000Z`);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Botón «Ver historial» al lado de un campo con `showHistory`: abre un popup con los valores
 * que fue teniendo, del original al actual. El dato sale de las copias que deja cada guardado.
 */
export default {
  name: "FieldHistoryButton",
  props: {
    modelName: { type: String, required: true },
    itemId: { type: String, required: true },
    /** Ruta del campo dentro del registro (`eta` o `grupo.eta`). */
    fieldPath: { type: String, required: true },
    field: { type: Object, required: true },
  },
  data() {
    return {
      open: false,
      loading: false,
      error: false,
      changes: [],
      labels: {},
      popupStyle: {},
    };
  },
  computed: {
    isDate() {
      return this.field.type === "date";
    },
    totalDiffLabel() {
      if (!this.isDate || this.changes.length < 2) return "";
      const diff = this.daysBetween(this.changes[0].value, this.changes[this.changes.length - 1].value);
      if (diff === null || diff === 0) return "";
      return this.$t("fieldHistory.sinceOriginal", { diff: this.signedDays(diff) });
    },
  },
  beforeDestroy() {
    this.unbind();
  },
  methods: {
    toggle() {
      if (this.open) {
        this.open = false;
        this.unbind();
        return;
      }
      this.placePopup();
      this.open = true;
      this.load();
      // En el próximo tick para que el mismo clic no lo cierre.
      setTimeout(() => {
        document.addEventListener("click", this.onOutsideClick, true);
        window.addEventListener("scroll", this.onScroll, true);
        window.addEventListener("resize", this.close);
      });
    },
    close() {
      this.open = false;
      this.unbind();
    },
    unbind() {
      document.removeEventListener("click", this.onOutsideClick, true);
      window.removeEventListener("scroll", this.onScroll, true);
      window.removeEventListener("resize", this.close);
    },
    /** Se cierra al mover la página (quedaría flotando lejos del campo), no al recorrer la lista. */
    onScroll(ev) {
      const popup = this.$refs.popup;
      if (popup && ev && popup.contains(ev.target)) return;
      this.close();
    },
    onOutsideClick(ev) {
      const popup = this.$refs.popup;
      const trigger = this.$refs.trigger;
      if (popup && popup.contains(ev.target)) return;
      if (trigger && trigger.contains(ev.target)) return;
      this.close();
    },
    /** Fijo en pantalla: los contenedores del formulario recortan lo que se sale. */
    placePopup() {
      const rect = this.$refs.trigger.getBoundingClientRect();
      const width = Math.min(340, window.innerWidth - 32);
      // Se abre hacia la derecha del botón: hacia la izquierda lo tapa el menú lateral.
      const left = Math.max(16, Math.min(rect.left - 8, window.innerWidth - width - 16));
      const below = window.innerHeight - rect.bottom;
      const style = { width: `${width}px`, left: `${left}px` };
      const above = rect.top;
      if (below < 260 && above > below) {
        style.bottom = `${window.innerHeight - rect.top + 4}px`;
        style.maxHeight = `${Math.min(360, above - 16)}px`;
      } else {
        style.top = `${rect.bottom + 4}px`;
        style.maxHeight = `${Math.min(360, below - 16)}px`;
      }
      this.popupStyle = style;
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${encodeURIComponent(this.modelName)}/${encodeURIComponent(this.itemId)}/field-history`,
          { params: { field: this.fieldPath } }
        );
        this.changes = data.changes || [];
        this.labels = data.labels || {};
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatOne(v) {
      if (v === null || v === undefined || v === "") return null;
      if (typeof v === "object" && v._id) v = v._id;
      const key = String(v);
      if (this.labels[key]) return this.labels[key];
      if (this.isDate) return formatDateSync(v, key);
      if (typeof v === "boolean") return v ? "✓" : "✗";
      if (typeof v === "number") return v.toLocaleString();
      if (typeof v === "object") return JSON.stringify(v);
      return key;
    },
    formatValue(value) {
      const list = Array.isArray(value) ? value : [value];
      const out = list.map(this.formatOne).filter((s) => s !== null);
      return out.length ? out.join(", ") : this.$t("fieldHistory.empty_value");
    },
    formatSavedAt(value) {
      return formatTimestampSync(value, "");
    },
    daysBetween(a, b) {
      const ma = calendarMs(a);
      const mb = calendarMs(b);
      if (ma === null || mb === null) return null;
      return Math.round((mb - ma) / DAY_MS);
    },
    signedDays(n) {
      return this.$t("fieldHistory.days", { n: n > 0 ? `+${n}` : String(n) });
    },
    stepDiffLabel(idx) {
      if (!this.isDate) return "";
      const diff = this.daysBetween(this.changes[idx - 1].value, this.changes[idx].value);
      return diff ? this.signedDays(diff) : "";
    },
  },
};
</script>

<style scoped>
.field-history-popup {
  position: fixed;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-align: left;
  font-weight: normal;
}
.field-history-popup .card-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.field-history-row + .field-history-row {
  border-top: 1px solid var(--bs-border-color);
}
</style>
